/**
 * Resumable chat turns — the router-path answer to "streams resume on
 * disconnect" (issue #27). A turn's loop events are teed into a store as they
 * stream; the turn keeps running under `ctx.waitUntil` when the client drops;
 * a reconnecting client replays the buffered tail by sequence number and
 * keeps following until the turn completes.
 *
 *   POST /chat/stream            → pumpBufferedTurn(...) + live NDJSON
 *   GET  /chat/stream/:turnId    → replayTurnEvents({ fromSeq }) → NDJSON
 *
 * Storage is a structural seam ({@link TurnEventStore}); a D1 implementation
 * ships here because that's what Cloudflare products have (KV is unsuitable:
 * eventually consistent cross-isolate). Per-token deltas would mean hundreds
 * of rows per turn, so consecutive text/reasoning deltas are coalesced within
 * a flush window before they are persisted — replay yields slightly chunkier
 * deltas with identical concatenation.
 */

export type TurnStatus = 'running' | 'complete' | 'error'

/** A running row is a renewable lease, not permanent truth. If the process
 *  driving a turn dies before writing a terminal status, reconnect discovery
 *  must eventually stop returning that abandoned row. */
export const DEFAULT_RUNNING_TURN_LEASE_MS = 5 * 60_000

/** Keep a healthy turn's lease comfortably ahead of expiry without turning
 *  per-token streaming into status-write traffic. */
export const DEFAULT_RUNNING_TURN_RENEW_INTERVAL_MS = 30_000

/** Represent a buffered turn event with a sequence number and serialized event data */
export interface BufferedTurnEvent {
  seq: number
  /** The serialized event line (JSON string, no trailing newline). */
  event: string
}

/** Manage and query turn events and their lifecycle statuses within a scoped event store */
export interface TurnEventStore {
  append(turnId: string, events: BufferedTurnEvent[]): Promise<void>
  read(turnId: string, fromSeq: number): Promise<BufferedTurnEvent[]>
  /** Record turn lifecycle. `scopeId` (a thread/session id) is optional and lets
   *  {@link TurnEventStore.listRunning} rediscover this turn after a client reload
   *  loses the turnId; stores that don't track scope ignore it. */
  setStatus(turnId: string, status: TurnStatus, scopeId?: string): Promise<void>
  getStatus(turnId: string): Promise<TurnStatus | null>
  /** Unexpired running turnIds for a scope, newest first — so a reloaded client
   *  (clientRunId lost) can find and resume the in-flight turn without reviving
   *  a row abandoned by a dead process. Optional: a store records it only if
   *  `setStatus` was given a `scopeId`. */
  listRunning?(scopeId: string): Promise<string[]>
  /** Explicitly remove one turn's events and status. Optional so existing
   *  custom stores remain source-compatible; the built-in D1 and memory stores
   *  provide it. */
  deleteTurn?(turnId: string): Promise<void>
  /** Remove terminal turns whose status was updated before `before` (Unix
   *  milliseconds or a Date). Running turns are never eligible. */
  pruneTerminalTurns?(before: number | Date): Promise<number>
}

/** Configure running-turn lease evaluation. The clock is injectable so store
 *  contract tests do not sleep. Keep the default in production unless a
 *  deployment also tunes the buffer's renewal interval. */
export interface TurnEventStoreOptions {
  runningTurnLeaseMs?: number
  now?: () => number
}

// ── coalescing ────────────────────────────────────────────────────────────

type AnyRecord = Record<string, unknown>

function deltaTypeOf(ev: unknown): 'text' | 'reasoning' | null {
  const e = ev as AnyRecord | null
  if (!e || typeof e !== 'object') return null
  const inner = (e.kind === 'event' ? (e.event as AnyRecord | undefined) : e) as AnyRecord | undefined
  if (!inner || typeof inner !== 'object') return null
  if ((inner.type === 'text' || inner.type === 'reasoning') && typeof inner.text === 'string') {
    return inner.type
  }
  return null
}

/** Merge consecutive text/reasoning deltas of the same type into one event.
 *  Concatenation-preserving: replaying the coalesced stream produces the same
 *  accumulated text as the original. */
export function coalesceDeltas(events: unknown[]): unknown[] {
  const out: unknown[] = []
  for (const ev of events) {
    const type = deltaTypeOf(ev)
    const prev = out[out.length - 1]
    if (type && prev && deltaTypeOf(prev) === type) {
      const read = (x: unknown): AnyRecord =>
        ((x as AnyRecord).kind === 'event' ? (x as AnyRecord).event : x) as AnyRecord
      const merged = JSON.parse(JSON.stringify(prev)) as AnyRecord
      read(merged).text = String(read(prev).text) + String(read(ev).text)
      out[out.length - 1] = merged
      continue
    }
    out.push(ev)
  }
  return out
}

function asPartUpdate(ev: unknown): { partId: unknown; delta: unknown } | null {
  const e = ev as AnyRecord | null
  if (!e || typeof e !== 'object' || e.type !== 'message.part.updated') return null
  const data = e.data as AnyRecord | undefined
  if (!data || typeof data !== 'object') return null
  const part = data.part as AnyRecord | undefined
  const partId = part?.id ?? data.partId ?? part?.partId ?? null
  return { partId, delta: data.delta }
}

/**
 * Coalesce consecutive `message.part.updated` deltas for the SAME part into one
 * event. agent-runtime products stream `ChatStreamEvent` NDJSON
 * (`{type:'message.part.updated', data:{part, delta}}`); pumped through the
 * buffer with the default tool-loop coalescer, every per-token delta persists as
 * its own row because that coalescer never recognizes the shape. Pass this as
 * {@link PumpBufferedTurnOptions.coalesce} instead.
 *
 * Concatenation-preserving for BOTH consumer styles: the merged event keeps the
 * LATEST event's `data.part` (already the cumulative accumulation) and sets
 * `data.delta` to the concatenation of the merged deltas, so a client that
 * appends `delta` and one that reads the cumulative `part` both reconstruct the
 * identical final text.
 */
export function coalesceChatStreamEvents(events: unknown[]): unknown[] {
  const out: unknown[] = []
  for (const ev of events) {
    const cur = asPartUpdate(ev)
    const prevEv = out[out.length - 1]
    const prev = prevEv ? asPartUpdate(prevEv) : null
    if (cur && prev && cur.partId != null && cur.partId === prev.partId) {
      // Base the merged row on the latest event (its `part` is the most complete
      // accumulation); carry forward the summed delta.
      const merged = JSON.parse(JSON.stringify(ev)) as AnyRecord
      ;(merged.data as AnyRecord).delta = String(prev.delta ?? '') + String(cur.delta ?? '')
      out[out.length - 1] = merged
      continue
    }
    out.push(ev)
  }
  return out
}

// ── buffering core (the tap) ────────────────────────────────────────────────

/** Define options for buffering and flushing turn events with optional live client delivery and event coalescing */
export interface BufferedTurnOptions {
  store: TurnEventStore
  turnId: string
  /** Deliver one serialized line to the live client. Throwing here (client
   *  disconnected) does NOT stop buffering — events keep persisting. */
  write?: (line: string) => Promise<void> | void
  /** Flush buffered events to the store at most this often. Default 400ms. */
  flushIntervalMs?: number
  /** Per-flush coalescer. Default {@link coalesceDeltas} (tool-loop text/reasoning
   *  deltas). agent-runtime products streaming `ChatStreamEvent` pass
   *  {@link coalesceChatStreamEvents} so per-token deltas don't each persist as a
   *  row. Must be concatenation-preserving. */
  coalesce?: (events: unknown[]) => unknown[]
  /** Optional scope (thread/session id) recorded with the turn status, so
   *  {@link TurnEventStore.listRunning} can find this turn after a reload. */
  scopeId?: string
  /** How often to renew the running-turn lease while a producer is alive.
   *  The timer is backed up by event-driven renewal when a runtime freezes
   *  unreferenced timers during remote I/O. Default
   *  {@link DEFAULT_RUNNING_TURN_RENEW_INTERVAL_MS}. */
  runningTurnRenewIntervalMs?: number
}

/** A push-driven buffer for a turn whose producer the caller does NOT own. */
export interface BufferedTurnTap {
  /** Buffer one event: persist (coalesced, on the flush window) + best-effort
   *  live-deliver. Wire to a push source's per-event hook (e.g. agent-runtime
   *  `handleChatTurn`'s `hooks.onEvent`). Marks the turn 'running' on first call. */
  onEvent(raw: unknown): Promise<void>
  /** Settle the turn: final flush + set status. Call after the producer resolves
   *  ('complete') or rejects ('error'). 'error' flushes what was produced first. */
  done(status?: Extract<TurnStatus, 'complete' | 'error'>): Promise<void>
}

/**
 * The buffering core. Sequence-numbers every event, delivers it to `write`
 * (best-effort — a disconnected client never stops buffering), and flushes to
 * the store in coalesced batches. Drives both transports:
 *
 *   • {@link pumpBufferedTurn}     — when you OWN an `AsyncIterable` producer.
 *   • this tap (`onEvent`/`done`)  — when the producer owns iteration and only
 *     hands you a push callback (agent-runtime `handleChatTurn`'s `hooks.onEvent`
 *     + the finished body). Durability stays here in the shell; the engine needs
 *     no `TurnEventStore` seam.
 */
export function createBufferedTurnTap(opts: BufferedTurnOptions): BufferedTurnTap {
  const flushIntervalMs = opts.flushIntervalMs ?? 400
  const coalesce = opts.coalesce ?? coalesceDeltas
  const startedAt = Date.now()
  let seq = 0
  let clientGone = false
  let pending: unknown[] = []
  let lastFlush = Date.now()
  let started = false
  let settled = false
  let renewalTimer: ReturnType<typeof setTimeout> | undefined
  let renewal: Promise<void> = Promise.resolve()
  let lastRenewedAt = 0
  const runningTurnRenewIntervalMs = Math.max(
    1,
    opts.runningTurnRenewIntervalMs ?? DEFAULT_RUNNING_TURN_RENEW_INTERVAL_MS,
  )

  function clearRenewalTimer(): void {
    if (renewalTimer !== undefined) clearTimeout(renewalTimer)
    renewalTimer = undefined
  }

  function scheduleRenewal(): void {
    if (settled || !opts.scopeId) return
    clearRenewalTimer()
    const elapsedMs = Math.max(0, Date.now() - lastRenewedAt)
    const delayMs = Math.max(1, runningTurnRenewIntervalMs - elapsedMs)
    renewalTimer = setTimeout(() => {
      renewalTimer = undefined
      void renewLease()
    }, delayMs)
    // A deliberately abandoned tap in a Node test must not keep the process
    // alive until the production renewal interval elapses.
    if (typeof renewalTimer === 'object' && 'unref' in renewalTimer) {
      renewalTimer.unref()
    }
  }

  async function renewLease(): Promise<void> {
    if (settled || !opts.scopeId) return
    clearRenewalTimer()
    lastRenewedAt = Date.now()
    renewal = renewal
      .then(() => opts.store.setStatus(opts.turnId, 'running', opts.scopeId))
      .catch(() => {})
    await renewal
    scheduleRenewal()
  }

  async function flush(): Promise<void> {
    if (pending.length === 0) return
    const batch = coalesce(pending)
    pending = []
    const rows = batch.map((ev) => ({ seq: ++seq, event: JSON.stringify(ev) }))
    await opts.store.append(opts.turnId, rows)
    lastFlush = Date.now()
  }

  async function ensureStarted(): Promise<void> {
    if (started) return
    started = true
    await opts.store.setStatus(opts.turnId, 'running', opts.scopeId)
    lastRenewedAt = Date.now()
    scheduleRenewal()
  }

  return {
    async onEvent(raw) {
      await ensureStarted()
      // Cloudflare may freeze an unreferenced timer while a producer is
      // awaiting remote I/O. A real event (including the route heartbeat)
      // therefore also renews any lease whose interval has elapsed.
      if (
        opts.scopeId &&
        Date.now() - lastRenewedAt >= runningTurnRenewIntervalMs
      ) {
        await renewLease()
      }
      // Stamp ms-since-turn-start so any stored turn is replayable AND traceable
      // (see ../trace) from the same buffered rows.
      const ev = raw && typeof raw === 'object' ? { ...(raw as Record<string, unknown>), _t: Date.now() - startedAt } : raw
      pending.push(ev)
      if (!clientGone && opts.write) {
        try {
          // Live delivery carries a provisional ordering hint, not the persisted
          // seq (coalescing changes seq assignment); clients resume with the
          // seqs from replay, or 0 for "everything".
          await opts.write(JSON.stringify(ev))
        } catch {
          clientGone = true
        }
      }
      if (Date.now() - lastFlush >= flushIntervalMs) await flush()
    },
    async done(status = 'complete') {
      await ensureStarted()
      settled = true
      clearRenewalTimer()
      await renewal
      if (status === 'error') {
        await flush().catch(() => {})
        await opts.store.setStatus(opts.turnId, 'error', opts.scopeId).catch(() => {})
        return
      }
      await flush()
      await opts.store.setStatus(opts.turnId, 'complete', opts.scopeId)
    },
  }
}

// ── pump (producer side) ──────────────────────────────────────────────────

/** Define options to pump data from an asynchronous iterable source with buffered turn control */
export interface PumpBufferedTurnOptions extends BufferedTurnOptions {
  source: AsyncIterable<unknown>
}

/**
 * Drive a turn to completion regardless of the live client, when you OWN the
 * producer as an `AsyncIterable`. A thin driver over {@link createBufferedTurnTap}.
 * Returns a promise that resolves when the turn finishes — hand it to
 * `ctx.waitUntil` so a disconnect can't kill the turn. Never rejects on
 * client-write failure; a source error marks the turn 'error' (after flushing
 * what was produced) and rethrows.
 */
export async function pumpBufferedTurn(opts: PumpBufferedTurnOptions): Promise<void> {
  const tap = createBufferedTurnTap(opts)
  try {
    for await (const raw of opts.source) await tap.onEvent(raw)
    await tap.done('complete')
  } catch (err) {
    await tap.done('error')
    throw err
  }
}

// ── replay (consumer side) ────────────────────────────────────────────────

/** Define options for replaying turn events with control over sequence, polling, and timeout */
export interface ReplayTurnEventsOptions {
  store: TurnEventStore
  turnId: string
  /** Replay strictly after this sequence number (0 = from the beginning). */
  fromSeq?: number
  /** Poll cadence while the turn is still running. Default 500ms. */
  pollMs?: number
  /** Give up following a 'running' turn after this long. Default 120s. */
  timeoutMs?: number
}

/**
 * Yield buffered events after `fromSeq`, then keep polling while the turn is
 * still 'running' until it completes, errors, or times out. Terminates with a
 * final `{seq: -1, event: '{"type":"turn_status",...}'}` marker so clients
 * know why the replay ended.
 */
export async function* replayTurnEvents(opts: ReplayTurnEventsOptions): AsyncGenerator<BufferedTurnEvent> {
  const pollMs = opts.pollMs ?? 500
  const timeoutMs = opts.timeoutMs ?? 120_000
  let cursor = opts.fromSeq ?? 0
  const deadline = Date.now() + timeoutMs

  for (;;) {
    const batch = await opts.store.read(opts.turnId, cursor)
    for (const row of batch) {
      cursor = Math.max(cursor, row.seq)
      yield row
    }
    const status = await opts.store.getStatus(opts.turnId)
    if (status !== 'running') {
      // Writers flush before publishing terminal status; include rows committed after the first read.
      yield* await opts.store.read(opts.turnId, cursor)
      yield { seq: -1, event: JSON.stringify({ type: 'turn_status', status: status ?? 'unknown' }) }
      return
    }
    if (Date.now() >= deadline) {
      yield { seq: -1, event: JSON.stringify({ type: 'turn_status', status: 'timeout' }) }
      return
    }
    await new Promise((r) => setTimeout(r, pollMs))
  }
}

/**
 * Serialize a replayed row for the wire, stamping the buffer ordinal ONTO the
 * line so a reconnecting client can continue from `?fromSeq=<lastSeq>`.
 *
 * The seq lives on the {@link BufferedTurnEvent} row wrapper, not inside the
 * serialized event — `flush()` builds `{seq: ++seq, event: JSON.stringify(ev)}`.
 * A route that enqueues `row.event` alone therefore emits lines with no seq at
 * all, and every client cursor silently pins to 0: each reconnect refetches the
 * whole turn and re-applies every delta onto already-rendered state. This
 * restores the contract `web-react/chat-stream` already documents ("replayed
 * lines carry an extra `seq` — transparently ignored").
 *
 * The `{seq: -1}` `turn_status` sentinel is passed through unstamped: it is a
 * terminator, not a cursor position, and stamping it would move a client's
 * cursor to -1.
 *
 * Fail-soft by construction — a line that is not a JSON object passes through
 * verbatim. A stamping bug must degrade to today's behaviour, never break a
 * replay.
 */
export function stampReplaySeq(row: BufferedTurnEvent): string {
  if (row.seq <= 0) return row.event
  const line = row.event
  // Cheap splice instead of parse+stringify: these rows are already canonical
  // JSON objects from `JSON.stringify`, and replay is a hot per-event path.
  if (line.charCodeAt(0) !== 0x7b /* { */) return line
  const rest = line.slice(1)
  return rest.trimStart().startsWith('}')
    ? `{"seq":${row.seq}${rest}`
    : `{"seq":${row.seq},${rest}`
}

// ── D1 store ──────────────────────────────────────────────────────────────

/** A bound D1 statement used by the turn store. */
export interface D1BoundForTurns {
  run(): Promise<unknown>
  all<T = Record<string, unknown>>(): Promise<{ results: T[] }>
  first<T = Record<string, unknown>>(): Promise<T | null>
}

/** Minimal structural D1 contract (Cloudflare `D1Database` satisfies it). */
export interface D1LikeForTurns {
  prepare(sql: string): {
    bind(...values: unknown[]): D1BoundForTurns
  }
  /** D1's only atomic primitive. Retention refuses a driver without it rather
   *  than deleting events and status in separate, reorderable requests. */
  batch?(statements: D1BoundForTurns[]): Promise<unknown[]>
}

function normalizeTurnCutoff(before: number | Date): { milliseconds: number; iso: string } {
  const milliseconds = before instanceof Date ? before.getTime() : before
  if (!Number.isFinite(milliseconds)) {
    throw new RangeError('turn retention cutoff must be a finite Unix-millisecond timestamp or a valid Date')
  }
  const date = new Date(milliseconds)
  if (!Number.isFinite(date.getTime())) {
    throw new RangeError('turn retention cutoff is outside the supported Date range')
  }
  return { milliseconds, iso: date.toISOString() }
}

async function runAtomicTurnBatch(
  db: D1LikeForTurns,
  statements: D1BoundForTurns[],
): Promise<unknown[]> {
  if (typeof db.batch !== 'function') {
    throw new Error('turn retention requires D1 batch() for atomic deletion')
  }
  return db.batch(statements)
}

function changesFromD1Result(result: unknown): number {
  if (!result || typeof result !== 'object') return 0
  const meta = (result as { meta?: unknown }).meta
  if (meta && typeof meta === 'object') {
    const changes = (meta as { changes?: unknown }).changes
    if (typeof changes === 'number' && Number.isFinite(changes)) return changes
  }
  const changes = (result as { changes?: unknown }).changes
  return typeof changes === 'number' && Number.isFinite(changes) ? changes : 0
}

/** Schema for the D1 store — append to the product's migrations. */
export const TURN_EVENTS_MIGRATION_SQL = `
CREATE TABLE IF NOT EXISTS turn_events (
  turnId TEXT NOT NULL,
  seq INTEGER NOT NULL,
  event TEXT NOT NULL,
  PRIMARY KEY (turnId, seq)
);
CREATE TABLE IF NOT EXISTS turn_status (
  turnId TEXT PRIMARY KEY,
  status TEXT NOT NULL,
  scopeId TEXT,
  updatedAt TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_turn_status_scope ON turn_status (scopeId, status);
CREATE INDEX IF NOT EXISTS idx_turn_status_retention ON turn_status (status, updatedAt);
`

/** For deployments whose `turn_status` table predates `scopeId`/`listRunning` —
 *  run once to add the column (the CREATE above already includes it for new
 *  deployments). SQLite ignores a duplicate-add error if already applied. */
export const TURN_STATUS_SCOPE_MIGRATION_SQL = `ALTER TABLE turn_status ADD COLUMN scopeId TEXT;`

/** For deployments that already have the turn tables, add the index used by
 *  terminal-turn retention without rerunning the table migration. */
export const TURN_STATUS_RETENTION_MIGRATION_SQL = 'CREATE INDEX IF NOT EXISTS idx_turn_status_retention ON turn_status (status, updatedAt);'

/** Resolve a TurnEventStore that appends and reads turn events using a D1-like database interface */
export function createD1TurnEventStore(
  db: D1LikeForTurns,
  options: TurnEventStoreOptions = {},
): TurnEventStore {
  const now = options.now ?? Date.now
  const runningTurnLeaseMs = Math.max(
    1,
    options.runningTurnLeaseMs ?? DEFAULT_RUNNING_TURN_LEASE_MS,
  )
  return {
    async append(turnId, events) {
      if (!events.length) return
      // One multi-row insert per flush window keeps write volume bounded.
      const placeholders = events.map(() => '(?, ?, ?)').join(', ')
      const values = events.flatMap((e) => [turnId, e.seq, e.event])
      await db.prepare(`INSERT OR IGNORE INTO turn_events (turnId, seq, event) VALUES ${placeholders}`).bind(...values).run()
    },
    async read(turnId, fromSeq) {
      const { results } = await db
        .prepare('SELECT seq, event FROM turn_events WHERE turnId = ? AND seq > ? ORDER BY seq ASC')
        .bind(turnId, fromSeq)
        .all<{ seq: number; event: string }>()
      return results
    },
    async setStatus(turnId, status, scopeId) {
      // COALESCE preserves a scopeId set on the initial 'running' write when a
      // later 'complete'/'error' write passes none.
      await db
        .prepare(
          'INSERT INTO turn_status (turnId, status, scopeId, updatedAt) VALUES (?, ?, ?, ?) ON CONFLICT(turnId) DO UPDATE SET status = excluded.status, scopeId = COALESCE(excluded.scopeId, turn_status.scopeId), updatedAt = excluded.updatedAt',
        )
        .bind(turnId, status, scopeId ?? null, new Date(now()).toISOString())
        .run()
    },
    async getStatus(turnId) {
      const row = await db.prepare('SELECT status FROM turn_status WHERE turnId = ?').bind(turnId).first<{ status: TurnStatus }>()
      return row?.status ?? null
    },
    async listRunning(scopeId) {
      const { results } = await db
        .prepare(
          "SELECT turnId FROM turn_status WHERE scopeId = ? AND status = 'running' AND updatedAt >= ? ORDER BY updatedAt DESC, rowid DESC",
        )
        .bind(scopeId, new Date(now() - runningTurnLeaseMs).toISOString())
        .all<{ turnId: string }>()
      return results.map((r) => r.turnId)
    },
    async deleteTurn(turnId) {
      await runAtomicTurnBatch(db, [
        db.prepare('DELETE FROM turn_events WHERE turnId = ?').bind(turnId),
        db.prepare('DELETE FROM turn_status WHERE turnId = ?').bind(turnId),
      ])
    },
    async pruneTerminalTurns(before) {
      const cutoff = normalizeTurnCutoff(before)
      const terminalBefore = "status IN ('complete', 'error') AND updatedAt < ?"
      const results = await runAtomicTurnBatch(db, [
        db
          .prepare(
            `DELETE FROM turn_events WHERE turnId IN (SELECT turnId FROM turn_status WHERE ${terminalBefore})`,
          )
          .bind(cutoff.iso),
        db.prepare(`DELETE FROM turn_status WHERE ${terminalBefore}`).bind(cutoff.iso),
      ])
      return changesFromD1Result(results[1])
    },
  }
}

/** In-memory store for tests and keyless local dev. */
export function createMemoryTurnEventStore(
  options: TurnEventStoreOptions = {},
): TurnEventStore {
  const events = new Map<string, BufferedTurnEvent[]>()
  const status = new Map<string, TurnStatus>()
  const scopes = new Map<string, string>()
  const order: string[] = []
  const updatedAt = new Map<string, number>()
  const now = options.now ?? Date.now
  const runningTurnLeaseMs = Math.max(
    1,
    options.runningTurnLeaseMs ?? DEFAULT_RUNNING_TURN_LEASE_MS,
  )
  return {
    async append(turnId, rows) {
      const list = events.get(turnId) ?? []
      list.push(...rows)
      events.set(turnId, list)
    },
    async read(turnId, fromSeq) {
      return (events.get(turnId) ?? []).filter((e) => e.seq > fromSeq)
    },
    async setStatus(turnId, s, scopeId) {
      status.set(turnId, s)
      if (scopeId) scopes.set(turnId, scopeId)
      if (!order.includes(turnId)) order.push(turnId)
      updatedAt.set(turnId, now())
    },
    async getStatus(turnId) {
      return status.get(turnId) ?? null
    },
    async listRunning(scopeId) {
      const cutoff = now() - runningTurnLeaseMs
      return order
        .filter(
          (turnId) =>
            status.get(turnId) === 'running' &&
            scopes.get(turnId) === scopeId &&
            (updatedAt.get(turnId) ?? Number.NEGATIVE_INFINITY) >= cutoff,
        )
        .sort((left, right) => {
          const updatedDelta = (updatedAt.get(right) ?? 0) - (updatedAt.get(left) ?? 0)
          return updatedDelta || order.indexOf(right) - order.indexOf(left)
        })
    },
    async deleteTurn(turnId) {
      events.delete(turnId)
      status.delete(turnId)
      scopes.delete(turnId)
      updatedAt.delete(turnId)
      const index = order.indexOf(turnId)
      if (index >= 0) order.splice(index, 1)
    },
    async pruneTerminalTurns(before) {
      const cutoff = normalizeTurnCutoff(before).milliseconds
      const removable = order.filter((turnId) => {
        const s = status.get(turnId)
        return (s === 'complete' || s === 'error') && (updatedAt.get(turnId) ?? Number.POSITIVE_INFINITY) < cutoff
      })
      for (const turnId of removable) {
        events.delete(turnId)
        status.delete(turnId)
        scopes.delete(turnId)
        updatedAt.delete(turnId)
      }
      if (removable.length) {
        const removed = new Set(removable)
        for (let i = order.length - 1; i >= 0; i--) {
          if (removed.has(order[i]!)) order.splice(i, 1)
        }
      }
      return removable.length
    },
  }
}
